package fr.frux.feature.mytrees

import androidx.compose.ui.graphics.vector.ImageVector
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.label
import fr.frux.feature.common.treeSeasonLabel
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.currentHarvestMonth
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.presentation.resolveTreeStatus
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/** Arbres créés par l'utilisateur — sous-page de Profil, pas un onglet. */
class MyTreesViewModel(
    private val treeRepository: TreeRepository,
    private val speciesRepository: SpeciesRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(MyTreesState())
    val state: StateFlow<MyTreesState> = _state.asStateFlow()

    /** Mois courant, substituable en test (même pattern que `MapViewModel.timeSource`). */
    internal var currentMonth: Int = currentHarvestMonth()

    /** Horloge, substituable en test (même pattern que [currentMonth], #444). */
    @OptIn(ExperimentalTime::class)
    internal var now: Instant = Clock.System.now()

    /** Fenêtres de récolte par espèce, dérivées du référentiel une fois chargé. */
    private var speciesHarvests: Map<Long, List<HarvestWindow>> = emptyMap()

    /** Dernier lot brut reçu, remappé si le référentiel arrive après lui. */
    private var rawTrees: List<Tree> = emptyList()

    init {
        loadSpecies()
        load()
    }

    /**
     * En vol, pour permettre à [load] d'être rappelé sans accumuler des requêtes concurrentes :
     * l'écran le fait à chaque remontée dans la composition (retour d'une fiche poussée par-dessus,
     * cf. [MyTreesScreen]), en plus de l'appel initial ci-dessus.
     */
    private var loadJob: Job? = null

    fun load() {
        loadJob?.cancel()
        loadJob =
            viewModelScope.launch {
                _state.update { it.copy(isLoading = true, error = null) }
                treeRepository.getMyTrees().collect { result ->
                    result.fold(
                        onSuccess = { trees ->
                            rawTrees = trees
                            _state.update { it.copy(trees = mapItems(trees), isLoading = false, error = null) }
                        },
                        onFailure = { error ->
                            _state.update { it.copy(isLoading = false, error = error.asAppError()) }
                        },
                    )
                }
            }
    }

    /**
     * Un échec du référentiel espèces ne doit jamais casser l'écran : la liste s'affiche quand
     * même, simplement sans libellé de saison (statut = mapping direct du statut déclaré).
     */
    private fun loadSpecies() {
        viewModelScope.launch {
            speciesRepository.getSpecies().fold(
                onSuccess = { species ->
                    speciesHarvests = species.associate { it.id to it.harvests }
                    _state.update { it.copy(trees = mapItems(rawTrees)) }
                },
                onFailure = { Logger.d(TAG, "⚠️ Référentiel espèces indisponible : ${it.message}") },
            )
        }
    }

    @OptIn(ExperimentalTime::class)
    private fun mapItems(trees: List<Tree>): List<MyTreeItem> =
        trees.map { tree ->
            val harvests = speciesHarvests[tree.species.id].orEmpty()
            val resolvedStatus = resolveTreeStatus(tree.status, harvests, currentMonth, tree.lastObservedAt, now)
            val seasonLabel = treeSeasonLabel(harvests, currentMonth)
            MyTreeItem(
                id = tree.id,
                name = tree.species.commonNameFr,
                meta = listOfNotNull(resolvedStatus.label, seasonLabel).joinToString(" · "),
                status = resolvedStatus,
                glyph = glyphFor(tree.species.scientificName, tree.harvestPart?.name),
                scientificName = tree.species.scientificName,
                photoUrl = tree.photoUrl,
            )
        }

    companion object {
        private const val TAG = "MyTreesViewModel"
    }
}

data class MyTreesState(
    val trees: List<MyTreeItem> = emptyList(),
    val isLoading: Boolean = false,
    /** Catégorie d'échec (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
)

data class MyTreeItem(
    val id: Long,
    val name: String,
    val meta: String,
    val status: FruxStatus,
    val glyph: ImageVector? = null,
    /** Pour teinter la pastille comme le marqueur (`treeMarkerTint`). */
    val scientificName: String = "",
    val photoUrl: String? = null,
)
